import React, { ForwardedRef, useState, useEffect, useRef } from 'react';
import Spacer from './Spacer';
import { PseudoLabel } from './PseudoLabel';
import { RepeatingActionsManager } from '../../classes/RepeatingActionsManager';
import { ButtonIconAnimations } from '../../globals'
import AnalyticsManager from '../../classes/AnalyticsManager';

interface ButtonProps
{
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	disabled?: boolean;
	cooldown?: number;
	/** when set to true, the button will not notify the analytics that it was clicked */
	silent?: boolean;
	category?: string;
	iconRenderer?: JSX.Element;
	cooldownAnimation?: ButtonIconAnimations;
	id?: string;
	className?: string;
	style?: React.CSSProperties;
	labelStyle?: React.CSSProperties;
	label?: string;
	active?: boolean;
	tooltip?: string;
	"data-name"?: string;
	["data-tooltip"]?: string;
	iconAfterLabel?: boolean;
}

const Button = React.forwardRef((props: ButtonProps, forwardedRef: ForwardedRef<HTMLDivElement>) =>
{
	const [buttonCooldown, setButtonCooldown] = useState(false);
	const ref = useRef<HTMLDivElement | null>(null);
	const componentIsMounted = useRef(true);
	useEffect(() =>
	{
		return () =>
		{
			componentIsMounted.current = false
		}
	}, [])

	//recursivly find the dom element of the button
	function getButtonDom(currentElement)
	{
		if (currentElement.classList.contains('button'))
		{
			return currentElement;
		}
		if (currentElement.parentElement)
		{
			return getButtonDom(currentElement.parentElement);
		}
		return null;
	}

	function buttonClicked(e: React.MouseEvent<HTMLDivElement, MouseEvent>)
	{
		if (props.onClick && !props.disabled && !buttonCooldown)
		{
			if (props.cooldown && props.cooldown > 0)
			{
				const buttonName = getButtonDom(e.target).dataset.name;
				function removeButtonCooldown()
				{
					if (componentIsMounted.current)
					{
						setButtonCooldown(false);
					}
					RepeatingActionsManager.removeAction(buttonName);
				}
				RepeatingActionsManager.setAction(removeButtonCooldown, props.cooldown, buttonName);
				setButtonCooldown(true);
			}

			if (!props.silent)
			{
				const buttonElement = getButtonDom(e.target);
				if (buttonElement)
				{
					AnalyticsManager.managedClick(buttonElement.dataset.name, props.category ?? "");
				}
			}
			props.onClick(e);
		}
	}

	const generateIconRenderer = (() =>
	{
		if (!props.iconRenderer) return null;
		if (!props.cooldownAnimation || !buttonCooldown) return props.iconRenderer;

		const iconRendererProps = { ...props.iconRenderer.props }
		switch (props.cooldownAnimation)
		{
			case ButtonIconAnimations.RotateLeft:
				iconRendererProps["className"] = "rotateLeft";
				break;
			case ButtonIconAnimations.RotateRight:
				iconRendererProps["className"] = "rotateRight";
				break;
		}

		return React.createElement(props.iconRenderer.type, iconRendererProps);
	})();

	return (
		<div
			ref={(node) => 
			{
				ref.current = node;
				if(typeof forwardedRef === "function") forwardedRef(node);
				else if(forwardedRef) forwardedRef.current = node;
			}}
			className={[
				"button",
				(props.className ? props.className : ""),
			].join(' ')}
			style={props.style}
			id={props.id ? props.id : ""}
			data-name={"button-" + (props["data-name"] ?? props.label)}
			data-status={props.disabled ? "disabled" : buttonCooldown ? "disabled" : "enabled"}
			data-active={props.active ? "true" : "false"}
			onClick ={buttonClicked}
			data-tooltip={props.tooltip ?? props["data-tooltip"]}
		>
			{
				!generateIconRenderer || props.iconAfterLabel ? null :
					<>
						{generateIconRenderer}
						{
							!props.label ? null :
								<Spacer size={{ x: "4px" }} />
						}
					</>
			}
			{
				!props.label ? null :
					<PseudoLabel value={props.label} style={props.labelStyle}/>
			}
			{
				!generateIconRenderer || !props.iconAfterLabel ? null :
					<>
						{
							!props.label ? null :
								<Spacer size={{ x: "4px" }} />
						}
						{generateIconRenderer}
					</>
			}
		</div>
	)
});

export default Button;